import { useMutation } from '@tanstack/react-query';
import { BrokerClientApi, PaymentApi } from '@apply/api';
import { PaymentReason, PaymentType } from '@apply/constants';
import { paymentAgreement } from '@apply/constants';
import { useCustomPayment } from './useCustomPayment';
import { applicationInsights } from '../../ApplicationInsights';

export const useSubmitCustomPayment = (paymentGuid: string) => {
    const [payment] = useCustomPayment(paymentGuid);

    const isValidExpiration = (date: string): boolean => {
        const regex = /^(0[1-9]|1[0-2])\/\d{2}$/;
        return regex.test(date);
    };

    return useMutation({
        mutationFn: async () => {
            if (!payment) throw new Error('Payment data not loaded');

            const paymentApi = new PaymentApi.PaymentApi(applicationInsights);
            const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);

            const { billingDetails, cardNumber, cardType, expirationDate, amount, paymentPurpose, propertyLoanReference, cvc } =
                payment;

            // Validate required billing fields again before submission
            if (
                !billingDetails?.name ||
                !billingDetails?.phone ||
                !billingDetails?.email ||
                !billingDetails?.address.line1 ||
                !billingDetails?.address.city ||
                !billingDetails?.address.state ||
                !billingDetails?.address.postal_code
            ) {
                throw new Error('Please complete all required billing details including address.');
            }

            // ───────────────────────────────────────────────
            // 1. ADDRESS VERIFICATION
            // ───────────────────────────────────────────────

            const address1 = billingDetails.address.line1 ?? '';
            const address2 = billingDetails.address.line2 ?? '';
            const combinedAddress = address2 ? `${address1} ${address2}` : address1;

            if (amount == undefined || amount == 0) {
                throw new Error('Payment Amount is missing.');
            }

            //Card Specific checks

            if (cardNumber == undefined || cardNumber == '') {
                throw new Error('Card Number not provided.');
            }

            if (expirationDate == undefined || !isValidExpiration(expirationDate)) {
                throw new Error('Expiration Date Not Provided.');
            }

            if (cvc == undefined || cvc == '') {
                throw new Error('CVC not provided');
            }

            const expirationTokens = expirationDate.split('/');

            const addressRequest: PaymentApi.AddressVerificationRequest = {
                paymentGuid: paymentGuid,
                cardNumber: cardNumber?.replace(/ /g, '') ?? '',
                creditCardType: cardType,
                expirationDate: `20${expirationTokens[1]}-${expirationTokens[0]}`,
                creditDebitType: 'CREDIT',
                address: combinedAddress,
                zipCode: billingDetails.address.postal_code ?? '',
                amount: amount,
            };

            const addressResponse = await paymentApi.verifyAddress(addressRequest);

            if (addressResponse.statusCode !== 200) throw new Error('There was a system error validating the address.');

            if (!addressResponse.result.isSuccess) throw new Error(addressResponse.result.errorMessage);

            // ───────────────────────────────────────────────
            // 2. IP ADDRESS LOOKUP
            // ───────────────────────────────────────────────

            const ipResponse = await brokerClientApi.getIpAddress();

            if (ipResponse.statusCode !== 200) throw new Error('Could not calculate IP address.');

            const ipAddress = ipResponse.result;

            // ───────────────────────────────────────────────
            // 3. AUTHORIZATION
            // (CVC not currently used per 1fb processing)
            // ───────────────────────────────────────────────

            const authorizationRequest: PaymentApi.AuthorizationRequest = {
                paymentGuid: paymentGuid,
                accountNumber: cardNumber?.replace(/ /g, '') ?? '',
                name: billingDetails.name ?? '',
                phone: billingDetails.phone ?? '',
                email: billingDetails.email ?? '',
                address1: billingDetails.address.line1 ?? '',
                address2: billingDetails.address.line2 ?? '',
                city: billingDetails.address.city ?? '',
                state: billingDetails.address.state ?? '',
                zip: billingDetails.address.postal_code ?? '',
                cardType: cardType,
                expirationDate: `20${expirationTokens[1]}-${expirationTokens[0]}`,
                amount: amount,
                avsAuthResponseCode: addressResponse.result.avsResponseCode,
                paymentReason: PaymentReason.Custom,
                memo: `Custom Payment (${paymentPurpose}): ${propertyLoanReference ?? ''}`,
                disclaimer: paymentAgreement,
                ipAddress: ipAddress,
                paymentHistoryType: PaymentType.CreditCard,
            };

            const authorizationResponse = await paymentApi.authorization(authorizationRequest);

            if (authorizationResponse.statusCode !== 200)
                throw new Error('There was a system error and the charge could not go through.');

            if (!authorizationResponse.result.isSuccess) throw new Error(authorizationResponse.result.errorMessage);

            return true;
        },
    });
};
